import {
	CurrencyConfigInput,
	InputMaybe,
	MultiCurrencyConfigInput,
} from "~/types/__generated/gql/graphql";
import { bigMath } from "~served/utils";
import { GetTillProductCategoriesResults } from "~/queries/useGetTillProductCategories";
import { $d } from "./currency-display";
import { DEFAULT_CURRENCY_CODE } from "~served/configs/constants";
import { GetFileByIdResults } from "~/queries/useGetFileById";
import Big from "big.js";

export const $dTillProductPrice = (
	product: GetTillProductCategoriesResults[0]["_products"][0],
	multiCurrencyConfig?: InputMaybe<MultiCurrencyConfigInput>,
) => {
	if (!multiCurrencyConfig) return $d(product.listedPrice);
	const matched = (product.priceConfigs ?? []).find(
		(c) => c.code === multiCurrencyConfig.selectedCurrency,
	);
	if (!matched) {
		const rate = multiCurrencyConfig.currencyConfigs.find(
			(c) => c.code === multiCurrencyConfig.selectedCurrency,
		)?.rate;
		if (!rate) return $d(product.listedPrice);

		return $d(
			bigMath.mul(product.listedPrice, rate),
			multiCurrencyConfig.selectedCurrency,
		);
	}
	return $d(matched.price, multiCurrencyConfig.selectedCurrency);
};

export const $dTillOptionPrice = (
	option: GetTillProductCategoriesResults[number]["_products"][number]["_options"][number],
	multiCurrencyConfig?: InputMaybe<MultiCurrencyConfigInput>,
) => {
	if (!multiCurrencyConfig) return $d(option.listedPrice);
	const matched = (option.priceConfigs ?? []).find(
		(c) => c.code === multiCurrencyConfig.selectedCurrency,
	);
	if (!matched) {
		const rate = multiCurrencyConfig.currencyConfigs.find(
			(c) => c.code === multiCurrencyConfig.selectedCurrency,
		)?.rate;
		if (!rate) return $d(option.listedPrice);

		return $d(
			bigMath.mul(option.listedPrice, rate),
			multiCurrencyConfig.selectedCurrency,
		);
	}
	return $d(matched.price, multiCurrencyConfig.selectedCurrency);
};

export interface RateToPrimaryDisplayResult {
	rate: number;
	currencyCode: string;
	primaryCurrencyCode: string;
	label: string;
}

const defaultRateToPrimaryDisplayResult: RateToPrimaryDisplayResult = {
	rate: 1,
	currencyCode: DEFAULT_CURRENCY_CODE,
	primaryCurrencyCode: DEFAULT_CURRENCY_CODE,
	label: `1 ${DEFAULT_CURRENCY_CODE} = 1 ${DEFAULT_CURRENCY_CODE}`,
};

export const $toPrimaryDisplay = (
	multiCurrencyConfig: InputMaybe<MultiCurrencyConfigInput>,
	primaryCurConfig?: CurrencyConfigInput,
): RateToPrimaryDisplayResult => {
	const res: RateToPrimaryDisplayResult = Object.assign(
		{},
		defaultRateToPrimaryDisplayResult,
	);

	if (!multiCurrencyConfig && !primaryCurConfig) {
		return res;
	}
	const selCurr = multiCurrencyConfig?.selectedCurrency;
	const selCurrConfig = multiCurrencyConfig?.currencyConfigs.find(
		(c) => c.code === selCurr,
	);

	if (selCurrConfig?.code) {
		res.currencyCode = selCurrConfig?.code ?? DEFAULT_CURRENCY_CODE;
	}

	if (primaryCurConfig?.code) {
		res.primaryCurrencyCode = primaryCurConfig?.code ?? DEFAULT_CURRENCY_CODE;
	}

	// if 1 USD = 0.5 EUR, then 1 EUR = 2 USD (1/0.5)
	res.rate = bigMath.divBig(
		primaryCurConfig?.rate ?? 1,
		selCurrConfig?.rate ?? 1,
	);
	res.label = `1 ${res.currencyCode} = ${new Big(res.rate ?? 0).round(2)} ${res.primaryCurrencyCode}`;

	return res;
};

export const $toOrderDisplay = (
	multiCurConfig?: GetFileByIdResults["multiCurrencyConfig"],
	orderCurConfig?: GetFileByIdResults["multiCurrencyConfig"],
): RateToPrimaryDisplayResult => {
	const res: RateToPrimaryDisplayResult = Object.assign(
		{},
		defaultRateToPrimaryDisplayResult,
	);

	if (!multiCurConfig && !orderCurConfig) {
		return res;
	}
	const selCurr = multiCurConfig?.selectedCurrency;
	const selCurrConfig = multiCurConfig?.currencyConfigs.find(
		(c) => c.code === selCurr,
	);
	const orderSelCurr = orderCurConfig?.selectedCurrency;
	const orderCurrConfig = orderCurConfig?.currencyConfigs.find(
		(c) => c.code === orderSelCurr,
	);

	if (selCurrConfig?.code) {
		res.currencyCode = selCurrConfig?.code ?? DEFAULT_CURRENCY_CODE;
	}

	if (orderCurrConfig?.code) {
		res.primaryCurrencyCode = orderCurrConfig?.code ?? DEFAULT_CURRENCY_CODE;
	}

	// if 1 USD = 0.5 EUR, then 1 EUR = 2 USD (1/0.5)
	// if 1 USD = 0.3 HKD, then 1 HKD = 1.67 EUR (0.5/0.3)
	res.rate = bigMath.div(orderCurrConfig?.rate ?? 1, selCurrConfig?.rate ?? 1);
	res.label = `1 ${res.currencyCode} = ${res.rate} ${res.primaryCurrencyCode}`;

	return res;
};
